import { DomainError } from '../errors/domain-error';
import { resolveMarketingPublicationBrandColor } from './marketing-brand';
import { appendAffiliateQueryParam } from './marketing-affiliate-url';
import {
  GROWTH_SPONSORSHIP_ADVERTISER_MAX,
  GROWTH_SPONSORSHIP_AFFILIATE_CODE_MAX,
  GROWTH_SPONSORSHIP_BODY_MAX,
  GROWTH_SPONSORSHIP_CTA_LABEL_MAX,
  GROWTH_SPONSORSHIP_DISCLOSURE_MAX,
  GROWTH_SPONSORSHIP_HEADLINE_MAX,
  normalizeGrowthAffiliateCode,
  type GrowthSponsorshipSlot,
} from '../growth/sponsorship-offer';

export const MARKETING_EDITION_BLOCK_TYPES = [
  'heading',
  'paragraph',
  'image',
  'button',
  'divider',
  'video',
  'footnote',
  'sponsorship',
] as const;

export type MarketingEditionBlockType = (typeof MARKETING_EDITION_BLOCK_TYPES)[number];

export type MarketingEditionBlock =
  | { id: string; type: 'heading'; level: 1 | 2 | 3; text: string }
  | { id: string; type: 'paragraph'; text: string }
  | { id: string; type: 'image'; url: string; alt?: string }
  | { id: string; type: 'button'; label: string; url: string; affiliateCode?: string }
  | { id: string; type: 'divider' }
  | { id: string; type: 'video'; url: string }
  | { id: string; type: 'footnote'; text: string }
  | {
      id: string;
      type: 'sponsorship';
      slot: 'primary' | 'secondary';
      advertiserName: string;
      headline: string;
      body?: string;
      imageUrl?: string;
      ctaLabel: string;
      ctaUrl: string;
      disclosure: string;
      offerId?: string;
      affiliateCode?: string;
    };

export type MarketingEditionBrand = {
  name: string;
  brandColor?: string | null;
  logoUrl?: string | null;
  fromName?: string | null;
  physicalAddress?: string | null;
};

export const NEWSLETTER_EDITION_STATUSES = [
  'draft',
  'scheduled',
  'sending',
  'sent',
  'failed',
  'cancelled',
] as const;

export type NewsletterEditionStatusValue = (typeof NEWSLETTER_EDITION_STATUSES)[number];

const MAX_BLOCKS = 80;
const MAX_TEXT = 20_000;
const MAX_URL = 2_000;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function newBlockId(): string {
  return `blk_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function assertHttpUrl(raw: string, field: string): string {
  const url = raw.trim();
  if (!url || url.length > MAX_URL) {
    throw new DomainError( `${field} is required`);
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new DomainError( `${field} must be a valid URL`);
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new DomainError( `${field} must use http or https`);
  }
  return parsed.toString();
}

function assertVideoEmbedUrl(raw: string): string {
  const url = assertHttpUrl(raw, 'Video URL');
  const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  const allowed =
    host === 'youtube.com' ||
    host === 'youtu.be' ||
    host === 'youtube-nocookie.com' ||
    host === 'vimeo.com' ||
    host.endsWith('.youtube.com') ||
    host.endsWith('.vimeo.com');
  if (!allowed) {
    throw new DomainError( 'Video embeds allow YouTube or Vimeo only');
  }
  return url;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeText(value: unknown, field: string): string {
  if (typeof value !== 'string') {
    throw new DomainError( `${field} must be a string`);
  }
  const text = value.trim();
  if (!text) {
    throw new DomainError( `${field} is required`);
  }
  if (text.length > MAX_TEXT) {
    throw new DomainError( `${field} is too long`);
  }
  return text;
}

export function createEmptyMarketingEditionBlocks(): MarketingEditionBlock[] {
  return [
    { id: newBlockId(), type: 'heading', level: 1, text: 'Untitled edition' },
    {
      id: newBlockId(),
      type: 'paragraph',
      text: 'Write your newsletter. Add an image and a call-to-action when you are ready.',
    },
  ];
}

export function assertNewsletterEditionStatus(
  value: string | null | undefined,
): NewsletterEditionStatusValue {
  if (!value || !(NEWSLETTER_EDITION_STATUSES as readonly string[]).includes(value)) {
    throw new DomainError( 'Invalid edition status');
  }
  return value as NewsletterEditionStatusValue;
}

function assertSponsorshipSlotLimits(blocks: MarketingEditionBlock[]) {
  const primary = blocks.filter((block) => block.type === 'sponsorship' && block.slot === 'primary').length;
  const secondary = blocks.filter((block) => block.type === 'sponsorship' && block.slot === 'secondary').length;
  if (primary > 1) {
    throw new DomainError('Only one primary sponsorship slot is allowed per edition');
  }
  if (secondary > 1) {
    throw new DomainError('Only one secondary sponsorship slot is allowed per edition');
  }
}

export function normalizeMarketingEditionBlocks(input: unknown): MarketingEditionBlock[] {
  if (!Array.isArray(input)) {
    throw new DomainError( 'Edition blocks must be an array');
  }
  if (input.length === 0) {
    throw new DomainError( 'Add at least one content block');
  }
  if (input.length > MAX_BLOCKS) {
    throw new DomainError( `Maximum ${MAX_BLOCKS} blocks allowed`);
  }

  const blocks = input.map((raw, index) => {
    if (!isPlainObject(raw) || typeof raw.type !== 'string') {
      throw new DomainError( `Block ${index + 1} is invalid`);
    }
    const id =
      typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim().slice(0, 64) : newBlockId();
    const type = raw.type as MarketingEditionBlockType;
    if (!(MARKETING_EDITION_BLOCK_TYPES as readonly string[]).includes(type)) {
      throw new DomainError( `Block type "${type}" is not allowed`);
    }

    switch (type) {
      case 'heading': {
        const levelRaw = Number(raw.level ?? 2);
        const level = ([1, 2, 3].includes(levelRaw) ? levelRaw : 2) as 1 | 2 | 3;
        return { id, type, level, text: normalizeText(raw.text, 'Heading') };
      }
      case 'paragraph':
        return { id, type, text: normalizeText(raw.text, 'Paragraph') };
      case 'image':
        return {
          id,
          type,
          url: assertHttpUrl(String(raw.url ?? ''), 'Image URL'),
          ...(typeof raw.alt === 'string' && raw.alt.trim()
            ? { alt: raw.alt.trim().slice(0, 200) }
            : {}),
        };
      case 'button': {
        const affiliateCode = normalizeGrowthAffiliateCode(
          typeof raw.affiliateCode === 'string' ? raw.affiliateCode : null,
        );
        return {
          id,
          type,
          label: normalizeText(raw.label, 'Button label').slice(0, 80),
          url: assertHttpUrl(String(raw.url ?? ''), 'Button URL'),
          ...(affiliateCode ? { affiliateCode } : {}),
        };
      }
      case 'sponsorship': {
        const slot: GrowthSponsorshipSlot = raw.slot === 'secondary' ? 'secondary' : 'primary';
        const affiliateCode = normalizeGrowthAffiliateCode(
          typeof raw.affiliateCode === 'string' ? raw.affiliateCode : null,
        );
        const body =
          typeof raw.body === 'string' && raw.body.trim()
            ? raw.body.trim().slice(0, GROWTH_SPONSORSHIP_BODY_MAX)
            : undefined;
        const imageUrl =
          typeof raw.imageUrl === 'string' && raw.imageUrl.trim()
            ? assertHttpUrl(raw.imageUrl, 'Sponsorship image URL')
            : undefined;
        return {
          id,
          type,
          slot,
          advertiserName: normalizeText(raw.advertiserName, 'Advertiser name').slice(
            0,
            GROWTH_SPONSORSHIP_ADVERTISER_MAX,
          ),
          headline: normalizeText(raw.headline, 'Sponsorship headline').slice(
            0,
            GROWTH_SPONSORSHIP_HEADLINE_MAX,
          ),
          ...(body ? { body } : {}),
          ...(imageUrl ? { imageUrl } : {}),
          ctaLabel: normalizeText(raw.ctaLabel, 'Sponsorship CTA label').slice(
            0,
            GROWTH_SPONSORSHIP_CTA_LABEL_MAX,
          ),
          ctaUrl: assertHttpUrl(String(raw.ctaUrl ?? ''), 'Sponsorship CTA URL'),
          disclosure: normalizeText(raw.disclosure ?? 'Sponsored', 'Sponsorship disclosure').slice(
            0,
            GROWTH_SPONSORSHIP_DISCLOSURE_MAX,
          ),
          ...(typeof raw.offerId === 'string' && raw.offerId.trim()
            ? { offerId: raw.offerId.trim().slice(0, 64) }
            : {}),
          ...(affiliateCode ? { affiliateCode } : {}),
        };
      }
      case 'divider':
        return { id, type };
      case 'video':
        return { id, type, url: assertVideoEmbedUrl(String(raw.url ?? '')) };
      case 'footnote':
        return { id, type, text: normalizeText(raw.text, 'Footnote').slice(0, 2_000) };
      default:
        throw new DomainError( `Block type "${type}" is not allowed`);
    }
  }) as MarketingEditionBlock[];
  assertSponsorshipSlotLimits(blocks);
  return blocks;
}

function renderBlockHtml(block: MarketingEditionBlock, accent: string): string {
  switch (block.type) {
    case 'heading': {
      const tag = block.level === 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3';
      const size = block.level === 1 ? '28px' : block.level === 2 ? '22px' : '18px';
      return `<${tag} style="margin:0 0 16px;font-size:${size};line-height:1.25;color:#111">${escapeHtml(block.text)}</${tag}>`;
    }
    case 'paragraph':
      return `<p style="margin:0 0 16px;font-size:16px;line-height:1.6;color:#222">${escapeHtml(block.text).replace(/\n/g, '<br/>')}</p>`;
    case 'image':
      return `<div style="margin:0 0 20px"><img src="${escapeHtml(block.url)}" alt="${escapeHtml(block.alt ?? '')}" style="display:block;max-width:100%;height:auto;border:0"/></div>`;
    case 'button': {
      const href = appendAffiliateQueryParam(block.url, block.affiliateCode);
      return `<div style="margin:0 0 24px"><a href="${escapeHtml(href)}" style="display:inline-block;background:${escapeHtml(accent)};color:#fff;text-decoration:none;padding:12px 20px;border-radius:6px;font-weight:600">${escapeHtml(block.label)}</a></div>`;
    }
    case 'sponsorship': {
      const href = appendAffiliateQueryParam(block.ctaUrl, block.affiliateCode);
      const image = block.imageUrl
        ? `<div style="margin:0 0 12px"><img src="${escapeHtml(block.imageUrl)}" alt="${escapeHtml(block.advertiserName)}" style="display:block;max-width:100%;height:auto;border:0;border-radius:6px"/></div>`
        : '';
      const body = block.body
        ? `<p style="margin:0 0 12px;font-size:15px;line-height:1.55;color:#333">${escapeHtml(block.body).replace(/\n/g, '<br/>')}</p>`
        : '';
      const slotLabel = block.slot === 'primary' ? 'Primary sponsorship' : 'Secondary sponsorship';
      return `<div data-sponsorship-slot="${escapeHtml(block.slot)}" style="margin:0 0 24px;padding:16px;border:1px solid #e8e8e8;border-radius:8px;background:#fafafa">
        <p style="margin:0 0 8px;font-size:11px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#888">${escapeHtml(slotLabel)} · ${escapeHtml(block.disclosure)}</p>
        <p style="margin:0 0 4px;font-size:13px;color:#666">${escapeHtml(block.advertiserName)}</p>
        <h3 style="margin:0 0 12px;font-size:20px;line-height:1.3;color:#111">${escapeHtml(block.headline)}</h3>
        ${image}
        ${body}
        <div style="margin:0 0 8px"><a href="${escapeHtml(href)}" style="display:inline-block;background:${escapeHtml(accent)};color:#fff;text-decoration:none;padding:10px 18px;border-radius:6px;font-weight:600">${escapeHtml(block.ctaLabel)}</a></div>
      </div>`;
    }
    case 'divider':
      return `<hr style="border:none;border-top:1px solid #e5e5e5;margin:24px 0"/>`;
    case 'video':
      return `<p style="margin:0 0 16px;font-size:15px;line-height:1.5"><a href="${escapeHtml(block.url)}" style="color:${escapeHtml(accent)}">Watch video</a></p>`;
    case 'footnote':
      return `<p style="margin:24px 0 0;font-size:12px;line-height:1.5;color:#666">${escapeHtml(block.text)}</p>`;
    default:
      return '';
  }
}

function renderBlockPlaintext(block: MarketingEditionBlock): string {
  switch (block.type) {
    case 'heading':
      return `${'#'.repeat(block.level)} ${block.text}`;
    case 'paragraph':
      return block.text;
    case 'image':
      return block.alt ? `[Image: ${block.alt}] ${block.url}` : `[Image] ${block.url}`;
    case 'button': {
      const href = appendAffiliateQueryParam(block.url, block.affiliateCode);
      return `${block.label}: ${href}`;
    }
    case 'sponsorship': {
      const href = appendAffiliateQueryParam(block.ctaUrl, block.affiliateCode);
      const lines = [
        `[${block.disclosure}] ${block.advertiserName} — ${block.headline}`,
        block.body,
        block.imageUrl ? `[Image] ${block.imageUrl}` : undefined,
        `${block.ctaLabel}: ${href}`,
      ].filter(Boolean);
      return lines.join('\n');
    }
    case 'divider':
      return '---';
    case 'video':
      return `Video: ${block.url}`;
    case 'footnote':
      return `* ${block.text}`;
    default:
      return '';
  }
}

export function renderMarketingEditionHtml(
  blocks: MarketingEditionBlock[],
  brand: MarketingEditionBrand,
): string {
  const accent = resolveMarketingPublicationBrandColor(brand.brandColor);
  const body = blocks.map((block) => renderBlockHtml(block, accent)).join('\n');
  const logo = brand.logoUrl
    ? `<img src="${escapeHtml(brand.logoUrl)}" alt="${escapeHtml(brand.name)}" style="max-height:48px;margin-bottom:16px"/>`
    : '';
  const address = brand.physicalAddress
    ? `<p style="margin:16px 0 0;font-size:11px;color:#888">${escapeHtml(brand.physicalAddress)}</p>`
    : '';
  return `<!doctype html>
<html>
<body style="margin:0;padding:0;background:#f6f6f6;font-family:Arial,Helvetica,sans-serif">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f6f6f6;padding:24px 12px">
    <tr><td align="center">
      <table role="presentation" width="100%" style="max-width:640px;background:#ffffff;border-radius:8px;padding:28px 24px">
        <tr><td>
          ${logo}
          <div style="height:4px;width:64px;background:${escapeHtml(accent)};border-radius:999px;margin:0 0 20px"></div>
          <p style="margin:0 0 20px;font-size:13px;color:#666">${escapeHtml(brand.fromName || brand.name)}</p>
          ${body}
          <hr style="border:none;border-top:1px solid #eee;margin:28px 0 12px"/>
          <p style="margin:0;font-size:12px;color:#888">You are receiving this because you subscribed to ${escapeHtml(brand.name)}. <a href="{{unsubscribe_url}}" style="color:${escapeHtml(accent)}">Unsubscribe</a> · <a href="{{preference_url}}" style="color:${escapeHtml(accent)}">Preferences</a></p>
          ${address}
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

export function renderMarketingEditionPlaintext(
  blocks: MarketingEditionBlock[],
  brand: MarketingEditionBrand,
): string {
  const lines = [
    brand.fromName || brand.name,
    brand.name,
    '',
    ...blocks.map(renderBlockPlaintext).filter(Boolean),
    '',
    '---',
    `Unsubscribe: {{unsubscribe_url}}`,
    `Preferences: {{preference_url}}`,
  ];
  if (brand.physicalAddress) lines.push(brand.physicalAddress);
  return lines.join('\n');
}
